import React, { useMemo } from 'react';
import PromotionalAnalysisControl from '../../ControlPanel/PromotionalAnalysisControl';
import PromotionalApexChart from '../../Charts/PromotionalApexChart';
import KPICards from '../../Charts/KPICards';
import { formatNum, pct } from '../../../utils_1/formatters';
import NPIBadge from '../../NPIBadge/NPIBadge';
import HelpTooltip from '../../HelpTooltip/HelpTooltip';
import { TOOLTIP_CONTENT } from '../../../constants/tooltipContent';
import './PromotionalWindow.css';
import { useState } from 'react';

const PromotionalWindow = ({
  onPromotionalForecast,
  loading,
  dataSummary,
  promotionalData
}) => {
  // Extract key data elements
  const {
    dates = [],
    base_demand_forecast = [],
    promotional_demand_forecast = [],
    confidence_upper = [],
    confidence_lower = [],
    regional_config = {},
    promotional_impact_analysis: pia = {},
    historical_summary: hist = {},
    data_quality: dq = {},
    data_source,
  } = promotionalData || {};

  const totalBase = base_demand_forecast.reduce((a, b) => a + b, 0);
  const totalPromo = promotional_demand_forecast.reduce((a, b) => a + b, 0);
  const totalDemand = totalBase + totalPromo;
  const liftPct = pia.avg_promotional_lift != null ? (pia.avg_promotional_lift * 100).toFixed(1) : null;
  const promoDays = promotional_demand_forecast.filter(v => v > 0).length;

  const [selectedSkuId, setSelectedSkuId] = useState('');

  const handlePromotionalForecast = (params) => {
    setSelectedSkuId(params.sku_id || '');
    onPromotionalForecast(params);
  };

  const kpiCards = useMemo(() => {
    if (!promotionalData) return [];
    return [
      {
        label: 'Total Analyzed Demand',
        value: formatNum(totalDemand),
        sub: `${dates.length} periods`,
        icon: '📦',
        color: 'blue',
        tooltip: TOOLTIP_CONTENT.total_forecast,
      },
      {
        label: 'Base Demand',
        value: formatNum(totalBase),
        sub: 'Non-promotional baseline',
        icon: '📈',
        color: 'teal',
        tooltip: TOOLTIP_CONTENT.base_demand,
      },
      {
        label: 'Promotional Demand',
        value: formatNum(totalPromo),
        sub: promoDays > 0 ? `${promoDays} active periods` : 'No promotions',
        icon: '🏷️',
        color: 'purple',
        tooltip: TOOLTIP_CONTENT.promo_demand,
      },
      {
        label: 'Average Lift',
        value: liftPct ? `+${liftPct}%` : '—',
        sub: 'Over base demand',
        icon: '🚀',
        color: liftPct && parseFloat(liftPct) > 20 ? 'orange' : 'green',
        tooltip: TOOLTIP_CONTENT.avg_lift,
      },
      {
        label: 'Promo Frequency',
        value: pct(pia.promotional_frequency),
        sub: 'Historical occurrences',
        icon: '📅',
        color: 'orange',
        tooltip: TOOLTIP_CONTENT.promo_frequency,
      },
      {
        label: 'Analyzed Records',
        value: formatNum(dq.historical_records_used),
        sub: data_source === 'synthetic_fallback' ? '⚠️ Synthetic' : '✅ Model-backed',
        icon: '🗄️',
        color: data_source === 'synthetic_fallback' ? 'red' : 'green',
        tooltip: TOOLTIP_CONTENT.historical_records,
      },
    ];
  }, [promotionalData, totalDemand, dates.length, totalBase, totalPromo, promoDays, liftPct, pia.promotional_frequency, dq.historical_records_used, data_source]);

  const modelingApproachStr = regional_config.promo_modeling_approach === 'embedded'
    ? 'Combined'
    : regional_config.promo_modeling_approach === 'hybrid'
      ? 'Hybrid'
      : 'Separated';
  const granularityStr = (regional_config.forecast_granularity || 'daily').charAt(0).toUpperCase() + (regional_config.forecast_granularity || 'daily').slice(1);
  const summaryLabel = regional_config.forecast_granularity === 'monthly'
    ? 'Monthly Summary'
    : regional_config.forecast_granularity === 'weekly'
      ? 'Weekly Summary'
      : 'Daily Summary';

  return (
    <div className="promotional-window">
      <div className="promo-header">
        <div className="promo-title-row">
          <div>
            <h2 className="promo-page-title">
              🏷️ Promotional Impact Analysis
            </h2>
            <p className="promo-page-sub">
              Evaluate how promotional events affect baseline demand patterns.
            </p>
          </div>

        </div>
      </div>

      <PromotionalAnalysisControl
        onPromotionalForecast={handlePromotionalForecast}
        loading={loading}
        dataSummary={dataSummary}
      />

      {selectedSkuId && <NPIBadge skuId={selectedSkuId} />}

      {promotionalData && kpiCards.length > 0 && (
        <KPICards cards={kpiCards} />
      )}

      {/* Main Chart */}
      <div className="promo-chart-card">
        <div className="chart-card-header">
          <div>
            <h3 className="chart-card-title">📉 Demand Breakdown Analysis</h3>
            {promotionalData && (
              <p className="chart-card-sub">
                {regional_config.region || 'Selected region'} • {granularityStr} Granularity • {modelingApproachStr} Approach
              </p>
            )}
          </div>
          {promotionalData && (
            <div className="chart-legend-pills">
              <span style={{
                fontSize: '0.72rem',
                padding: '2px 8px',
                borderRadius: '20px',
                background: 'rgba(37,99,235,0.10)',
                color: '#2563EB',
                border: '1px solid rgba(37,99,235,0.20)',
                fontWeight: 600,
              }}>
                {summaryLabel}
              </span>
              <span className="pill pill-blue">■ Base</span>
              <span className="pill pill-purple">■ Promo</span>
              <span className="pill pill-green">── Total</span>
              <span style={{
                fontSize: '0.72rem',
                padding: '2px 8px',
                borderRadius: '20px',
                background: 'rgba(245,158,11,0.12)',
                color: '#F59E0B',
                border: '1px solid rgba(245,158,11,0.3)',
                fontWeight: 600,
              }}>
                Confidence Band
              </span>
            </div>
          )}
        </div>
        <div className="chart-card-body">
          {loading ? (
            <div className="chart-loading-state">
              <div className="loading-spinner" />
              <p>Analyzing promotional impact…</p>
            </div>
          ) : promotionalData ? (
            <PromotionalApexChart
              dates={dates}
              baseDemand={base_demand_forecast}
              promoDemand={promotional_demand_forecast}
              confidenceUpper={confidence_upper}
              confidenceLower={confidence_lower}
              granularity={regional_config.forecast_granularity || 'daily'}
              modelingApproach={regional_config.promo_modeling_approach || 'separated'}
              region={regional_config.region || ''}
            />
          ) : null}
        </div>
      </div>

      {promotionalData && (
        <div className="promo-details-grid">
          <div className="promo-detail-card">
            <div className="detail-header">
              <h4>📊 Advanced Metrics</h4>
              <span className="detail-badge purple">{modelingApproachStr}</span>
            </div>
            <div className="detail-list">
              <div className="detail-item">
                <span className="dl-label">Historical Base Avg:</span>
                <span className="dl-value">{formatNum(hist.historical_base_avg, 1)}</span>
              </div>
              <div className="detail-item">
                <span className="dl-label">Historical Promo Avg:</span>
                <span className="dl-value">{formatNum(hist.historical_promo_avg, 1)}</span>
              </div>
              <div className="detail-item">
                <span className="dl-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  Promo to Base Ratio:
                  <HelpTooltip {...TOOLTIP_CONTENT.promo_base_ratio} />
                </span>
                <span className="dl-value">{hist.promotional_vs_base_ratio ? hist.promotional_vs_base_ratio.toFixed(2) + 'x' : '—'}</span>
              </div>
              <div className="detail-item">
                <span className="dl-label">Calendar Entries Used:</span>
                <span className="dl-value">{dq.promotional_calendar_entries || 0}</span>
              </div>
            </div>
          </div>

          <div className="promo-detail-card">
            <div className="detail-header">
              <h4>🔍 Data Quality</h4>
              <span className="detail-badge teal">{dq.date_range_analyzed ? `${dq.date_range_analyzed.start} to ${dq.date_range_analyzed.end}` : ''}</span>
            </div>
            <div className="detail-list">
              <div className="detail-item">
                <span className="dl-label">Total Records Analyzed:</span>
                <span className="dl-value">{formatNum(dq.historical_records_used)}</span>
              </div>
              <div className="detail-item">
                <span className="dl-label">Base Period Records:</span>
                <span className="dl-value">{formatNum(dq.base_period_records)}</span>
              </div>
              <div className="detail-item">
                <span className="dl-label">Promo Period Records:</span>
                <span className="dl-value">{formatNum(dq.promotional_records_found)}</span>
              </div>
              <div className="detail-item">
                <span className="dl-label">Data Source Used:</span>
                <span className="dl-value source-val">{data_source === 'synthetic_fallback' ? 'Synthetic Engine' : 'Historical Pipeline'}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {!promotionalData && !loading && (
        <div className="promo-empty-state">
          <div className="empty-icon">🏷️</div>
          <h3>Ready for analysis</h3>
          <p>Select location, grouping, and methodology to see how promotions affect demand.</p>
          <div className="empty-features">
            <div className="empty-feature">🇰🇷 Separate vs Embedded promotional mapping</div>
            <div className="empty-feature">🇨🇳 Regional constraints and granularities</div>
            <div className="empty-feature">📈 Advanced lift and frequency computations</div>
            <div className="empty-feature">🎯 Cross-verified with base demand history</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromotionalWindow;
